import { Truck, Ban } from "lucide-react";
import { sizeLabel } from "@/lib/sizeLabels";

const STATUS_CLASSES = {
  Available: "bg-emerald-950/40 border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/60",
  Occupied: "bg-red-950/40 border-red-500/50 text-red-300 hover:bg-red-900/60",
  Reserved: "bg-amber-950/40 border-amber-500/50 text-amber-300 hover:bg-amber-900/60",
  OutOfService: "bg-slate-800/50 border-slate-600/50 text-slate-500 opacity-60",
};

export default function SpotTile({ spot, session, onClick, compact = false, orientation = "horizontal", dim = false }) {
  const isOverstay = session?.status === "OverstayPendingCharge";
  const flag = session?.customer?.flag;
  const watch = flag === "WatchList";
  const vip = flag === "VIP";
  const status = spot.status;
  const sizePad = compact ? "p-1 min-h-[58px]" : "p-2 min-h-[92px]";
  const base =
    `relative flex h-full w-full flex-col items-center justify-center rounded-lg border-2 transition-colors select-none ${sizePad}`;
  const interactive = status === "OutOfService" ? "cursor-not-allowed" : "cursor-pointer";
  const overstayRing = isOverstay
    ? "overstay-pulse ring-2 ring-red-500 border-red-500 z-10"
    : "";
  const watchRing = watch && !isOverstay ? "ring-2 ring-amber-400 animate-pulse z-10" : "";
  const dimClass = dim ? "opacity-25" : "";
  const rot =
    orientation === "diagonal"
      ? "rotate-[18deg]"
      : orientation === "vertical"
      ? ""
      : "";

  const tenancy = spot.tenancyType;
  const tenancyClass =
    tenancy === "Monthly" ? "bg-blue-500 text-white" : "bg-red-500 text-white";
  const iconSize = compact ? "h-4 w-4" : "h-6 w-6";
  const numSize = compact ? "text-[9px]" : "text-xs";

  return (
    <button
      type="button"
      data-testid={`spot-tile-${spot.spotNumber}`}
      onClick={() => status !== "OutOfService" && onClick(spot, session)}
      className={`${base} ${STATUS_CLASSES[status]} ${interactive} ${overstayRing} ${watchRing} ${dimClass} ${rot}`}
    >
      <span className={`absolute left-1 top-1 font-data ${numSize} font-bold text-slate-100`}>
        {spot.spotNumber}
      </span>

      {watch && (
        <span
          data-testid={`watch-flag-${spot.spotNumber}`}
          className="absolute -top-1.5 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-1.5 text-[8px] font-bold text-black"
        >
          WATCH
        </span>
      )}
      {vip && !watch && (
        <span
          data-testid={`vip-flag-${spot.spotNumber}`}
          className="absolute -top-1.5 left-1/2 -translate-x-1/2 rounded-full bg-yellow-400 px-1.5 text-[8px] font-bold text-black"
        >
          VIP
        </span>
      )}

      {(status === "Occupied" || status === "Reserved") && (
        <span
          className={`absolute right-1 top-1 rounded-sm px-1 py-0.5 text-[8px] font-bold tracking-wider ${tenancyClass}`}
        >
          {tenancy === "Monthly" ? "M" : "D"}
        </span>
      )}

      <div className={`${compact ? "mt-1.5" : "mt-2"} flex flex-col items-center`}>
        {status === "OutOfService" ? (
          <Ban className={iconSize} />
        ) : (
          <Truck className={iconSize} />
        )}
        {!compact && (
          <span className="mt-1 text-center text-[10px] font-medium leading-tight">
            {sizeLabel(spot.sizeClass)}
          </span>
        )}
      </div>

      {isOverstay && (
        <span className="absolute -bottom-1 rounded-full bg-red-500 px-1.5 text-[8px] font-bold text-white">
          OVERSTAY
        </span>
      )}
      {session?.paymentFailed && (
        <span
          data-testid={`spot-payment-failed-${spot.id}`}
          title="Payment failed — needs attention"
          className="absolute -right-1.5 -top-1.5 z-20 animate-pulse rounded-full bg-amber-500 px-1 text-[7px] font-black leading-tight text-slate-950"
        >
          PAY FAILED
        </span>
      )}
    </button>
  );
}
